/**
 * Live `progress` notes from Connie, Tia, and Salma streams.
 * Notes are ephemeral: callers keep them on the in-memory turn only.
 *
 * A note starts on the first `progress` after summary/document context,
 * tool_start, tool_result, or text_delta. Consecutive `progress` events
 * append to that note. The note ends at the next tool_start, text_delta,
 * done, or error. The status line stays up through tool calls and clears
 * on text_delta, done, and error.
 */

export type ProgressNotes = {
  /** Open note being concatenated. Null when the next progress starts a new note. */
  draft: string | null;
  /** Latest note shown under the reply. Null once cleared. */
  status: string | null;
  /** Closed notes for this live turn, in order. */
  notes: string[];
};

export function createProgressNotes(): ProgressNotes {
  return { draft: null, status: null, notes: [] };
}

export function appendProgressNote(
  state: ProgressNotes,
  delta: string
): ProgressNotes {
  if (!delta) return state;
  const draft = state.draft == null ? delta : `${state.draft}${delta}`;
  return { ...state, draft, status: draft };
}

/** End the open note. The status line keeps showing the latest note. */
export function closeProgressNote(state: ProgressNotes): ProgressNotes {
  if (state.draft == null) return state;
  const note = state.draft.trim();
  if (!note) {
    return { ...state, draft: null, status: null };
  }
  return {
    draft: null,
    status: note,
    notes: [...state.notes, note],
  };
}

/**
 * Text Connie streamed before a tool call ("I'll search the Library…") is
 * narration, not the reply: FSI saves only the text after the last tool as the
 * turn's answer. Moving it into the notes keeps the live turn and history the
 * same, and keeps it readable as the status line while the tool runs.
 */
export function demoteReplyToNote(
  state: ProgressNotes,
  reply: string
): ProgressNotes {
  const closed = closeProgressNote(state);
  const note = reply.trim();
  if (!note) return closed;
  return { draft: null, status: note, notes: [...closed.notes, note] };
}

/** End the open note and hide the status line. */
export function clearProgressStatus(state: ProgressNotes): ProgressNotes {
  const closed = closeProgressNote(state);
  return { ...closed, status: null };
}

export function progressMessageFields(state: ProgressNotes): {
  progressStatus?: string;
  progressNotes?: string[];
} {
  return {
    progressStatus: state.status ?? undefined,
    progressNotes: state.notes.length ? state.notes : undefined,
  };
}
